import { VariableNameFinder } from './variable-name-finder.ts';
import type { ReleaseProfileFormula } from '../../types/release-profiles.ts';

export function nameToVariableName(name: string): string {
  return name
    .trim()
    .replace(/\s+/g, '_')
    .replace(/__+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '')
    .replace(/_+$/, '')
    .replace(/^_+/, '');
}

const productChemicalConcentrationLabel = 'Chemical Concentration in Product (%)';
export const productChemicalConcentrationVar = nameToVariableName(productChemicalConcentrationLabel); // Chemical_Concentration_in_Product
const productElementalConcentrationLabel = 'Elemental Concentration in Product (%)';
export const productElementalConcentrationVar = nameToVariableName(productElementalConcentrationLabel); // Elemental_Concentration_in_Product

const productUsageLbLabel = 'Product Usage (lb)';
export const productUsageLbVar = nameToVariableName(productUsageLbLabel); // Product_Usage_lb
const productUsageGalLabel = 'Product Usage (gal)';
export const productUsageGalVar = nameToVariableName(productUsageGalLabel); // Product_Usage_gal
const productSolidsPercentOfWeightLabel = 'Product Solids (Percent of Weight)';
export const productSolidsPercentOfWeightVar = nameToVariableName(productSolidsPercentOfWeightLabel); // Product_Solids_Percent_of_Weight
const productSolidsPercentOfVolumeLabel = 'Product Solids (Percent of Volume)';
export const productSolidsPercentOfVolumeVar = nameToVariableName(productSolidsPercentOfVolumeLabel); // Product_Solids_Percent_of_Volume

const rainfallLabel = 'Rainfall';
export const rainfallVar = nameToVariableName(rainfallLabel); // Rainfall

const notApplicableLabel = 'Not Applicable';
export const notApplicableVar = nameToVariableName(notApplicableLabel); // Not_Applicable

export const wasteStreamAllocationPercentageVar = nameToVariableName('Waste Stream Allocation Percentage');

export interface PredefinedVariable {
  nameFriendly: string;
  nameForVariable: string;
  description: string;
  source: string;
  units: string;
  frequencyConfigurable?: boolean;
}

export const formulaPredefinedVariables: PredefinedVariable[] = [
  {
    nameFriendly: productChemicalConcentrationLabel,
    nameForVariable: productChemicalConcentrationVar,
    description:
      'Uses the concentration of each chemical in the assigned products to quantify releases from any associated product.',
    source: 'Product Data',
    units: '%',
  },
  {
    nameFriendly: productElementalConcentrationLabel,
    nameForVariable: productElementalConcentrationVar,
    description:
      'For metal compounds (lead, etc.), uses the elemental concentration of each chemical in the assigned products to quantify releases from any associated product.',
    source: 'Product Data',
    units: '%',
  },
  {
    nameFriendly: productSolidsPercentOfWeightLabel,
    nameForVariable: productSolidsPercentOfWeightVar,
    description: 'Uses the solids percentage by weight of the assigned products.',
    source: 'Product Data',
    units: '%',
  },
  {
    nameFriendly: productSolidsPercentOfVolumeLabel,
    nameForVariable: productSolidsPercentOfVolumeVar,
    description: 'Uses the solids percentage by volume of the assigned products.',
    source: 'Product Data',
    units: '%',
  },
  {
    nameFriendly: productUsageLbLabel,
    nameForVariable: productUsageLbVar,
    description:
      'Uses the mass of product used in the production process as defined in the products section of the application.',
    source: 'Product Usage Records',
    units: 'pounds',
    frequencyConfigurable: true,
  },
  {
    nameFriendly: productUsageGalLabel,
    nameForVariable: productUsageGalVar,
    description:
      'Uses the volume of product used in the production process as defined in the products section of the application.',
    source: 'Product Usage Records',
    units: 'gallons',
    frequencyConfigurable: true,
  },
  {
    nameFriendly: rainfallLabel,
    nameForVariable: rainfallVar,
    description: 'Represents rainfall data used as an environmental factor in calculations.',
    source: 'Weather Data',
    units: 'inches',
    frequencyConfigurable: true,
  },
  {
    nameFriendly: notApplicableLabel,
    nameForVariable: notApplicableVar,
    description: 'Implies no release occurred. Used in formula choices.',
    source: 'Facility Response',
    units: 'none',
  },
];

export function variablesImplyProduct(variables: Set<string>): boolean {
  return (
    variables.has(productUsageLbVar) ||
    variables.has(productUsageGalVar) ||
    variablesImplyChemicalConcentration(variables) ||
    variables.has(productSolidsPercentOfWeightVar) ||
    variables.has(productSolidsPercentOfVolumeVar)
  );
}

export function variablesImplyChemicalConcentration(variables: Set<string>): boolean {
  return variables.has(productChemicalConcentrationVar) || variables.has(productElementalConcentrationVar);
}

export function formulaStringImpliesProduct(formula: string): boolean {
  return variablesImplyProduct(VariableNameFinder.findInStringIgnoringErrors(formula));
}

/**
 * Recursively collects all variable names referenced by a formula,
 * following references to other formulas in the elaborated set.
 */
export function getAllReferencedVarNames(
  formulaString: string,
  allFormulas: ReleaseProfileFormula['formulas'],
  visited = new Set<string>(),
): Set<string> {
  const directNames = VariableNameFinder.findInStringIgnoringErrors(formulaString);
  const allNames = new Set(directNames);
  for (const otherFormula of Object.values(allFormulas)) {
    if (directNames.has(otherFormula.nameForVariable) && !visited.has(otherFormula.nameForVariable)) {
      visited.add(otherFormula.nameForVariable);
      for (const name of getAllReferencedVarNames(otherFormula.formula, allFormulas, visited)) {
        allNames.add(name);
      }
    }
  }
  return allNames;
}
